import { atomWithDebounce, removeDiacritics } from '@rematter/ui-web';
import { atom } from 'jotai';
import keyBy from 'lodash/keyBy';

import {
  bookingListAtom,
  BookingRecord,
  loadListAtom,
  LoadRecord,
  salesOrderListAtom,
  SalesOrderRecord,
} from '../_graphql/queryObjectList';

import { ObjectListEntry } from './_types';
import { $selectedConversation } from './conversationList.state';

export const $objectsSearchTerm = atomWithDebounce('', 300);

const loadFilterKeywords = /(load|loads|ld)/gi;
const salesOrderFilterKeywords = /(salesorder|sales order|so)/gi;
const bookingFilterKeywords = /(booking|bkg|bk)/gi;

export const $isLoadingObjects = atom((get) => {
  const loadList = get($filteredLoadList);
  const salesOrderList = get($filteredSalesOrderList);
  const bookingList = get($filteredBookingList);

  return (
    loadList.isLoading || salesOrderList.isLoading || bookingList.isLoading
  );
});

export const $isFetchingObjects = atom((get) => {
  const loadList = get($filteredLoadList);
  const salesOrderList = get($filteredSalesOrderList);
  const bookingList = get($filteredBookingList);

  return (
    loadList.isFetching || salesOrderList.isFetching || bookingList.isFetching
  );
});

export const $clearSearchTerm = atom((get) => {
  const rawSearchTerm = get($objectsSearchTerm.value);
  return rawSearchTerm
    .replace(loadFilterKeywords, '')
    .replace(salesOrderFilterKeywords, '')
    .replace(bookingFilterKeywords, '');
});

export const $filteredLoadList = loadListAtom((get) => ({
  variables: {
    searchTerm: get($objectsSearchTerm.debouncedValue),
  },
}));

export const $filteredSalesOrderList = salesOrderListAtom((get) => ({
  variables: {
    searchTerm: get($objectsSearchTerm.debouncedValue),
  },
}));

export const $filteredBookingList = bookingListAtom((get) => ({
  variables: {
    searchTerm: get($objectsSearchTerm.debouncedValue),
  },
}));

const $selectedConversationLoads = loadListAtom((get) => {
  const loadIds = (get($selectedConversation)?.custom.objectsData ?? [])
    .filter((it) => it.type === 'load')
    .map((it) => it.id);

  return { variables: { ids: loadIds }, skip: loadIds.length === 0 };
});

const $selectedConversationSalesOrders = salesOrderListAtom((get) => {
  const salesOrderIds = (get($selectedConversation)?.custom.objectsData ?? [])
    .filter((it) => it.type === 'salesorder')
    .map((it) => it.id);

  return {
    variables: { ids: salesOrderIds },
    skip: salesOrderIds.length === 0,
  };
});

const $selectedConversationBookings = bookingListAtom((get) => {
  const bookingIds = (get($selectedConversation)?.custom.objectsData ?? [])
    .filter((it) => it.type === 'booking')
    .map((it) => it.id);

  return {
    variables: { ids: bookingIds },
    skip: bookingIds.length === 0,
  };
});

const loadRecordToObjectListEntry = (load: LoadRecord): ObjectListEntry => ({
  id: load.id,
  type: 'load' as const,
  title: 'LOAD-' + String(load.documentNumber).padStart(5, '0'),
  status: load.status,
  subtitle: load.accountName ?? '',
});

const salesOrderRecordToObjectListEntry = (
  salesOrder: SalesOrderRecord,
): ObjectListEntry => ({
  id: salesOrder.id,
  type: 'salesorder' as const,
  title: String(salesOrder.documentNumber),
  status: salesOrder.status,
  subtitle: salesOrder.accountName ?? '',
});

const bookingRecordToObjectListEntry = (
  booking: BookingRecord,
): ObjectListEntry => ({
  id: booking.id,
  type: 'booking' as const,
  title: String(booking.bookingNumber),
  status: booking.status,
  subtitle: booking.destinationPortName ?? '',
});

export const $selectedConversationObjectsById = atom((get) => {
  const loads = get($selectedConversationLoads).data ?? [];
  const salesOrders = get($selectedConversationSalesOrders).data ?? [];
  const bookings = get($selectedConversationBookings).data ?? [];

  return keyBy(
    [
      ...loads.map(loadRecordToObjectListEntry),
      ...salesOrders.map(salesOrderRecordToObjectListEntry),
      ...bookings.map(bookingRecordToObjectListEntry),
    ],
    (it) => it.id,
  );
});

export const $filteredObjectList = atom((get): ObjectListEntry[] => {
  const loadList = get($filteredLoadList);
  const salesOrderList = get($filteredSalesOrderList);
  const bookingList = get($filteredBookingList);
  const selectedConversation = get($selectedConversation);

  const existingObjectIds = new Set(
    selectedConversation?.custom.objectsData.map((it) => it.id) ?? [],
  );

  const rawSearchTerm = removeDiacritics(
    get($objectsSearchTerm.debouncedValue).toLowerCase(),
  );

  const loadsOnly = loadFilterKeywords.test(rawSearchTerm);
  const salesOrdersOnly = salesOrderFilterKeywords.test(rawSearchTerm);
  const bookingsOnly = bookingFilterKeywords.test(rawSearchTerm);

  const loadListEntries =
    loadList.data
      ?.map(loadRecordToObjectListEntry)
      .filter((it) => !existingObjectIds.has(it.id)) ?? [];

  const salesOrderListEntries =
    salesOrderList.data
      ?.map(salesOrderRecordToObjectListEntry)
      .filter((it) => !existingObjectIds.has(it.id)) ?? [];

  const bookingListEntries =
    bookingList.data
      ?.map(bookingRecordToObjectListEntry)
      .filter((it) => !existingObjectIds.has(it.id)) ?? [];

  if (loadsOnly) {
    return loadListEntries;
  }

  if (salesOrdersOnly) {
    return salesOrderListEntries;
  }

  if (bookingsOnly) {
    return bookingListEntries;
  }

  return [...loadListEntries, ...salesOrderListEntries, ...bookingListEntries];
});
